import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { env } from '../../../../../environments/environment';

import { FlagService } from '../../../../flag/flag.service';
import { ToastrService } from 'ngx-toastr';
import { FlagStatus, Inspection, UnderwritingStatus } from '../../../../shared/interfaces';
import { FlagManagerService } from '../../flag-manager.service';
import { Subscription } from 'rxjs';
import { FlagManagerIssueDetailComponent } from '../flag-manager-issue-detail/flag-manager-issue-detail.component';
import Sortable from 'sortablejs';

@Component({
  selector: 'app-flag-image-section',
  templateUrl: './flag-image-section.component.html',
  styleUrls: ['./flag-image-section.component.scss'],
})
export class FlagImageSectionComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  @Input() flags: any[] = [];
  @Input() inspection: Inspection;
  @Input() underwritingStatus: UnderwritingStatus;
  @Output() selectedFlagsChange = new EventEmitter<number[]>();
  @Output() statusChange = new EventEmitter<{ flagId: number; status: number }>();
  @Output() flagIgnored = new EventEmitter<any>();

  @Input() showResolveOptions: boolean = false;
  @Input() resolveBarSelectedTab: string = '';
  @Input() selectedTab: string = '';

  @ViewChild('issueDetailModal', { static: false }) issueDetailModal!: FlagManagerIssueDetailComponent;
  @ViewChild('imageGrid', { static: false }) imageGridRef!: ElementRef<HTMLDivElement>;

  selectedFlagIds: number[] = [];
  selectedPicture: any;
  selectedFlag: any;
  exif;
  private sortable?: Sortable;

  constructor(
    private flagService: FlagService,
    private toastr: ToastrService,
    private flagManagerService: FlagManagerService,
    private router: Router,
  ) {}

  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    const selectAllSub = this.flagManagerService.getSelectAllState().subscribe(selectAll => {
      if (selectAll) {
        this.selectAllFlags();
      } else {
        this.deselectAllFlags();
      }
    });

    const selectedIdsSub = this.flagManagerService.getSelectedFlagIds().subscribe(flagIds => {
      this.selectedFlagIds = [...flagIds];
      this.updateCheckboxes();
      this.selectedFlagsChange.emit([...this.selectedFlagIds]);
    });

    this.subscriptions.push(selectAllSub, selectedIdsSub);
  }

  ngAfterViewInit(): void {
    this.initSortable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectedFlagIds = [];
    if (changes['flags'] || changes['underwritingStatus']) {
      if (changes['flags']) {
        this.refreshAllImages();
        this.flags = this.flags.map(flag => {
          flag.checkbox = new UntypedFormControl(false);

          flag.checkbox.valueChanges.subscribe((checked: boolean) => {
            this.onCheckboxChange(flag.id, checked);
          });

          const flagWithCheckbox = {
            ...flag,
            ...(flag.groupId ? {} : { showInfo: false }),
          };

          return flagWithCheckbox;
        });
      }

      // Destroy and reinitialize Sortable after the grid renders (flags toggles *ngIf)
      // This ensures sortable is properly reinitialized when flags or status change
      setTimeout(() => {
        this.initSortable();
      }, 0);
    }
  }

  onCheckboxChange(flagId: number, isChecked: boolean) {
    if (isChecked) {
      if (!this.selectedFlagIds.includes(flagId)) {
        this.selectedFlagIds.push(flagId);
      }
    } else {
      this.selectedFlagIds = this.selectedFlagIds.filter(id => id !== flagId);
    }
    this.selectedFlagsChange.emit([...this.selectedFlagIds]);
  }

  selectAllFlags(): void {
    if (this.flags) {
      this.selectedFlagIds = this.flags.map(flag => flag.id);
      this.updateCheckboxes();
      this.selectedFlagsChange.emit([...this.selectedFlagIds]);
    }
  }

  deselectAllFlags(): void {
    this.selectedFlagIds = [];
    this.updateCheckboxes();
    this.selectedFlagsChange.emit([...this.selectedFlagIds]);
  }

  updateCheckboxes(): void {
    if (this.flags) {
      this.flags.forEach(flag => {
        if (flag.checkbox) {
          flag.checkbox.setValue(this.selectedFlagIds.includes(flag.id), { emitEvent: false });
        }
      });
    }
  }

  ngDoCheck() {
    this.flags?.forEach(flag => {
      const pictureId = flag?.picture?.id;
      if (!pictureId) return;

      if (!flag.imageUrl || flag._needsImageRefresh) {
        const timestamp = flag.picture?.updatedAt || Date.now();
        flag.imageUrl = `${env.APIUrl}/image/embedded/${pictureId}?t=${timestamp}`;
        flag._needsImageRefresh = false;
      }
    });
  }

  violationData = {
    violation_level_1: 'Immediate action required',
    violation_level_2: 'Recommended to reduce the risk of loss',
    violation_level_3: 'Recommendation',
  };

  getViolationValue(key: string) {
    if (this.violationData.hasOwnProperty(key)) {
      return this.violationData[key];
    } else {
      return null;
    }
  }

  openPicture(flag) {
    this.selectedPicture = flag.picture;
    this.selectedFlag = flag;
  }

  get anyCheckboxChecked(): boolean {
    return this.flags?.some(flag => flag.checkbox?.value);
  }

  embeddedUrl(picture) {
    return `${env.APIUrl}/image/embedded/${picture?.id}`;
  }

  updateThumb(picture) {
    const flagsWithSamePicture = this.flags.filter(f => f.picture?.id === picture.id);
    flagsWithSamePicture.forEach(flag => {
      if (flag) {
        flag._needsImageRefresh = true;
        flag.picture = { ...flag.picture, ...picture };
        flag.picture.updatedAt = picture.updatedAt || Date.now();
        const timestamp = flag.picture.updatedAt;
        flag.imageUrl = `${env.APIUrl}/image/embedded/${picture.id}?t=${timestamp}`;
      }
    });

    this.flagManagerService.setFlagPictureUpdates(picture);
  }

  async moveFlag({ flag, x, y }) {
    this.selectedFlag.x = x;
    this.selectedFlag.y = y;

    await this.flagService.edit(flag.id, { ...flag, x, y });
    this.toastr.success('Flag Saved');

    this.updateThumb(flag.picture);
  }

  onResolveFlag(flag: any) {
    this.statusChange.emit({ flagId: flag.id, status: FlagStatus.RESOLVED });
  }

  onPartialResolveFlag(flag: any) {
    this.statusChange.emit({ flagId: flag.id, status: FlagStatus.PARTIAL_RESPONSE });
  }

  onUnresolveFlag(flag: any) {
    this.statusChange.emit({ flagId: flag.id, status: FlagStatus.UNRESOLVED });
  }

  openFlagChangelog(flag: any) {
    this.selectedFlag = flag;
    this.issueDetailModal.openModal();
  }

  async onIgnoreFlag(flag: any) {
    try {
      // Determine the action based on selected tab
      if (this.selectedTab === 'excluded') {
        // When on included tab, this should un-ignore (include) the flag
        flag.ignore = false;
        if (flag.flag) {
          flag.flag.ignore = false;
        }
      } else {
        // When on excluded tab or other tabs, this should ignore (exclude) the flag
        flag.ignore = true;
        if (flag.flag) {
          flag.flag.ignore = true;
        }
      }

      this.flagIgnored.emit(flag);
    } catch (error) {
      console.error('Error toggling flag ignore status:', error);
      this.toastr.error('Failed to update flag status');
    }
  }

  refreshAllImages() {
    // Force refresh all flag images
    this.flags?.forEach(flag => {
      if (flag.picture?.id) {
        flag._needsImageRefresh = true;
      }
    });
  }

  onQuestion(flag: any) {
    if (this.inspection?.id) {
      const deeplink = flag.deeplink;
      const url = this.router.createUrlTree([`/inspection/review/main/${this.inspection.id}`], {
        queryParams: {
          item: deeplink[0],
          section: deeplink[1],
          question: deeplink[2],
          right: 'question',
        },
      });

      window.open(this.router.serializeUrl(url), '_blank');
    } else {
      this.toastr.warning('Inspection not found');
    }
  }

  ngOnDestroy(): void {
    this.destroySortable();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  shouldShowFlagViolationDeadline(flag: any): boolean {
    if (!flag?.groupId || flag.subtype?.type?.name !== 'Violation' || !flag?.violation?.deadline || !this.inspection) {
      return false;
    }

    const flagDeadlineDate = flag.violation.deadline.split('T')[0];

    const actionRequiredDate = this.inspection.violationActionRequiredDeadline
      ? new Date(this.inspection.violationActionRequiredDeadline).toISOString().split('T')[0]
      : null;
    const recommendedDate = this.inspection.violationRecommendedDeadline
      ? new Date(this.inspection.violationRecommendedDeadline).toISOString().split('T')[0]
      : null;

    const isDifferentFromAction = !actionRequiredDate || flagDeadlineDate !== actionRequiredDate;
    const isDifferentFromRecommended = !recommendedDate || flagDeadlineDate !== recommendedDate;

    return isDifferentFromAction && isDifferentFromRecommended;
  }

  private destroySortable(): void {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = undefined;
    }
  }

  private initSortable(): void {
    // Destroy existing sortable instance if it exists
    this.destroySortable();

    if (!this.imageGridRef?.nativeElement) {
      return;
    }

    // Disable drag-and-drop if status is "Rec Letter Sent" or beyond
    if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) {
      return;
    }

    this.sortable = Sortable.create(this.imageGridRef.nativeElement, {
      animation: 150,
      ghostClass: 'flag-drag-ghost',

      group: { name: 'flags-xfer', pull: true, put: false },
      handle: '.cta-drag-handle',
      draggable: '.image-card',

      onEnd: evt => {
        const oldIndex = evt.oldIndex ?? -1;
        const newIndex = evt.newIndex ?? -1;
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          return;
        }

        // Get the flag that was at the new position before the move
        const flagAtNewPosition = this.flags[newIndex];

        // Swap the flags in the array
        const moved = this.flags.splice(oldIndex, 1)[0];
        this.flags.splice(newIndex, 0, moved);

        // Swap order numbers if both flags have groupId and order
        if (moved?.id && moved?.groupId && flagAtNewPosition?.id && flagAtNewPosition?.groupId) {
          this.swapFlagOrders(moved, flagAtNewPosition);
        } else if (moved?.id && moved?.groupId) {
          // If only moved flag has groupId, use the old move API
          this.updateFlagOrder(moved.id, newIndex, moved.groupId);
        }
      },
    });
  }

  private async swapFlagOrders(movedFlag: any, targetFlag: any): Promise<void> {
    try {
      // Get current order values
      const movedOrder = movedFlag.order ?? movedFlag.flag?.order;
      const targetOrder = targetFlag.order ?? targetFlag.flag?.order;

      // If orders are the same, no need to swap
      if (movedOrder === targetOrder) {
        return;
      }

      // Swap order values locally
      if (movedFlag.order !== undefined) {
        movedFlag.order = targetOrder;
      }
      if (movedFlag.flag?.order !== undefined) {
        movedFlag.flag.order = targetOrder;
      }

      if (targetFlag.order !== undefined) {
        targetFlag.order = movedOrder;
      }
      if (targetFlag.flag?.order !== undefined) {
        targetFlag.flag.order = movedOrder;
      }

      // Update both flags via API
      const movedFlagData = { ...movedFlag };
      delete movedFlagData.checkbox;
      const targetFlagData = { ...targetFlag };
      delete targetFlagData.checkbox;

      await Promise.all([
        this.flagService.edit(movedFlag.id, movedFlagData),
        this.flagService.edit(targetFlag.id, targetFlagData),
      ]);

      // Notify service to update flags in all components
      this.flagManagerService.notifyFlagOrderUpdated(movedFlag.id, targetOrder);
      this.flagManagerService.notifyFlagOrderUpdated(targetFlag.id, movedOrder);
      this.flagManagerService.notifyFlagsUpdated();

      this.toastr.success('Flag order updated');
    } catch (error) {
      console.error('Error swapping flag orders:', error);
      this.toastr.error('Failed to update flag order');
    }
  }

  private async updateFlagOrder(flagId: number, newPosition: number, targetGroupId: number): Promise<void> {
    try {
      await this.flagService.move(flagId, newPosition, targetGroupId);
      this.toastr.success('Flag order updated');
    } catch (error) {
      console.error('Error updating flag order:', error);
      this.toastr.error('Failed to update flag order');
    }
  }

  markOrientation(e: Event) {
    const img = e.target as HTMLImageElement;
    const isPortrait = img.naturalHeight > img.naturalWidth;
    img.classList.toggle('portrait', isPortrait);
    img.style.display = 'block';
  }
}
